import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

// ── Global Diagnostics & Crash Logger Interceptor ──
(function setupDiagnosticsLogger() {
  const LOGS_API = '/api/logs';
  const logBuffer = [];
  const sessionId = 'session_' + Math.random().toString(36).substring(2, 10);
  
  // Track active flushes to avoid parallel collision
  let isFlushing = false;

  // Preserve native console methods
  const nativeLog = console.log;
  const nativeWarn = console.warn;
  const nativeError = console.error;
  const nativeDebug = console.debug;

  const pushToBuffer = (level, args) => {
    try {
      // Format the arguments cleanly
      const message = args.map(arg => {
        if (arg instanceof Error) return arg.message + '\n' + arg.stack;
        if (typeof arg === 'object') {
          try { return JSON.stringify(arg); } catch { return String(arg); }
        }
        return String(arg);
      }).join(' ');

      // Guard against logging our own API interactions to prevent infinite feedback loops
      if (message.includes('/api/logs') || message.includes('[Logs Ingest]') || message.includes('console_diagnostics')) {
        return;
      }

      logBuffer.push(JSON.stringify({
        level,
        message,
        session: sessionId,
        timestamp: new Date().toISOString()
      }));

      // Cap local buffer in case backend is offline
      if (logBuffer.length > 500) {
        logBuffer.shift();
      }
    } catch (e) {
      // Fail silently to never interrupt actual app runtime
    }
  };

  // Monkey-patch console endpoints
  console.log = (...args) => {
    nativeLog.apply(console, args);
    pushToBuffer('info', args);
  };

  console.warn = (...args) => {
    nativeWarn.apply(console, args);
    pushToBuffer('warn', args);
  };

  console.error = (...args) => {
    nativeError.apply(console, args);
    pushToBuffer('error', args);
  };

  console.debug = (...args) => {
    nativeDebug.apply(console, args);
    pushToBuffer('debug', args);
  };

  // Intercept global uncaught errors
  window.addEventListener('error', (event) => {
    const errorDetails = event.error 
      ? { message: event.error.message, stack: event.error.stack }
      : { message: event.message };
    
    pushToBuffer('error', [`[CRITICAL CRASH] Uncaught window error:`, event.message, errorDetails]);
  });

  // Intercept unhandled promise rejections (failed network calls, async crashes)
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg = reason instanceof Error 
      ? reason.message + '\n' + reason.stack 
      : String(reason);
    
    pushToBuffer('error', [`[ASYNC REJECTION] Unhandled promise rejection:`, msg]);
  });

  // Flush buffer to VPS backend every 3 seconds
  setInterval(async () => {
    if (logBuffer.length === 0 || isFlushing) return;
    isFlushing = true;
    
    const chunk = logBuffer.splice(0, logBuffer.length);
    const payload = chunk.join('\n');
    
    try {
      const token = localStorage.getItem('bfs_session_token');
      const headers = { 'Content-Type': 'text/plain' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(LOGS_API, {
        method: 'POST',
        headers,
        body: payload,
        // KeepAlive is supported in fetch to ensure it outlives page reloads if navigating
        keepalive: true
      });
    } catch (e) {
      // Put logs back in buffer if request fails due to offline state
      logBuffer.unshift(...chunk);
    } finally {
      isFlushing = false;
    }
  }, 3000);

  nativeLog(`[console_diagnostics] Active and tracking under session ID: ${sessionId}`);
})();

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
